// ITAR License Exemptions — 22 CFR Parts 123, 124, 125, and 126.
// Structured for the /tools/itar-license-exemptions menu + detail pages.
//
// Where a single regulatory section groups multiple distinct exemptions
// (e.g. § 123.16(b), § 125.4(b)), each numbered sub-exemption is a separate
// `subs` entry so users can drill straight into the one they need.
//
// Descriptions are compressed summaries — verify against the eCFR before use.

export const ITAR_EXEMPTION_GROUPS = [
  {
    id: "temporary-import-export",
    label: "Temporary Import & Export",
    kicker: "Movements of hardware that come back — or leave and come back — subject to strict return timelines.",
    items: [
      {
        code: "§ 123.4",
        slug: "123-4",
        name: "Temporary Import Exemptions",
        cfr: "22 CFR § 123.4",
        description: "Two temporary-import pathways — unclassified U.S.-origin defense items with subsequent export (§ 123.4(a)) and unclassified defense articles temporarily imported for incorporation, modification, or servicing (§ 123.4(b)).",
        subs: [
          {
            code: "§ 123.4(a)",
            slug: "123-4-a",
            name: "Temporary Import and Subsequent Export of U.S.-Origin Items",
            cfr: "22 CFR § 123.4(a)",
            description: "Temporary import of U.S.-origin items followed by subsequent export.",
          },
          {
            code: "§ 123.4(b)",
            slug: "123-4-b",
            name: "Temporary Import for Incorporation, Modification, or Servicing",
            cfr: "22 CFR § 123.4(b)",
            description: "Temporary import (only — not the subsequent export) of unclassified defense articles that will be incorporated, modified, enhanced, altered, or serviced in a way that changes basic performance. A DSP-5 license is required for the export.",
          },
        ],
      },
      {
        code: "§ 123.16(b)",
        slug: "123-16",
        name: "Special Export Exemptions",
        cfr: "22 CFR § 123.16(b)",
        description: "A grab-bag of narrow export pathways — small-value shipments, packing cases, mock-ups, trade shows, and intra-company temporary exports.",
        subs: [
          {
            code: "§ 123.16(b)(1)",
            slug: "123-16-b-1",
            name: "Exports Under an ITAR Agreement",
            cfr: "22 CFR § 123.16(b)(1)",
            description: "Exports of defense articles in furtherance of an approved TAA, MLA, or WDA.",
          },
          {
            code: "§ 123.16(b)(2)",
            slug: "123-16-b-2",
            name: "Parts or Components Under $500 to a Previously Approved End-User",
            cfr: "22 CFR § 123.16(b)(2)",
            description: "Parts or components valued at not more than $500 per transaction, exported to support a previously authorized defense article to a previously approved end-user (not a distributor). No more than 24 shipments per calendar year.",
          },
          {
            code: "§ 123.16(b)(3)",
            slug: "123-16-b-3",
            name: "Packing Cases Specially Designed for Defense Articles",
            cfr: "22 CFR § 123.16(b)(3)",
            description: "Packing cases specially designed to hold defense articles.",
          },
          {
            code: "§ 123.16(b)(4)",
            slug: "123-16-b-4",
            name: "Inoperable Models and Mock-Ups",
            cfr: "22 CFR § 123.16(b)(4)",
            description: "Inoperable models and mock-ups that reveal no technical data.",
          },
          {
            code: "§ 123.16(b)(5)",
            slug: "123-16-b-5",
            name: "Temporary Exports to Public Trade Shows",
            cfr: "22 CFR § 123.16(b)(5)",
            description: "Temporary exports to public trade shows of items previously authorized.",
          },
          {
            code: "§ 123.16(b)(9)",
            slug: "123-16-b-9",
            name: "Temporary Export to a Subsidiary, Affiliate, or Facility",
            cfr: "22 CFR § 123.16(b)(9)",
            description: "Temporary export of unclassified components, parts, tools, or test equipment by a U.S. person to a subsidiary, affiliate, or facility it owns or controls, for use in manufacture, assembly, testing, production, or modification.",
          },
        ],
      },
      {
        code: "§ 123.17",
        slug: "123-17",
        name: "Body Armor and Other Personal Protective Gear",
        cfr: "22 CFR § 123.17",
        description: "Temporary personal export by an individual of one set of body armor (which may include a helmet) or one set of chemical agent protective gear, for that person's exclusive use — declared to CBP at each departure and returned at the end of the trip.",
      },
    ],
  },
  {
    id: "reexports-retransfers",
    label: "Reexports & Retransfers",
    kicker: "Movement of previously exported defense articles between foreign parties.",
    items: [
      {
        code: "§ 123.9(e)",
        slug: "123-9-e",
        name: "Reexport of U.S.-Origin Components Incorporated Into a Foreign Defense Article",
        cfr: "22 CFR § 123.9(e)",
        description: "Reexport or retransfer of U.S.-origin components incorporated into a foreign defense article to NATO members, or the governments of Australia, Israel, Japan, New Zealand, or the Republic of Korea — without prior DDTC approval, subject to a 30-day post-reexport notification and SME, MTCR, and high-value contract exclusions.",
      },
    ],
  },
  {
    id: "training-services",
    label: "Training & Defense Services",
    kicker: "Defense services and training exemptions under Part 124.",
    items: [
      {
        code: "§ 124.2(a)",
        slug: "124-2-a",
        name: "Training on Basic Operations and Maintenance",
        cfr: "22 CFR § 124.2(a)",
        description: "Training on basic operations and maintenance of defense articles previously authorized for export.",
      },
      {
        code: "§ 124.2(b)",
        slug: "124-2-b",
        name: "Military Service by U.S. Person in Foreign Military",
        cfr: "22 CFR § 124.2(b)",
        description: "Military service by a U.S. person drafted into a foreign military.",
      },
      {
        code: "§ 124.2(c)",
        slug: "124-2-c",
        name: "Maintenance Technical Data or Defense Services to NATO / AJS",
        cfr: "22 CFR § 124.2(c)",
        description: "Technical data or defense services related to maintenance exported to NATO countries or Australia, Japan, or Sweden.",
      },
    ],
  },
  {
    id: "technical-data",
    label: "Technical Data & Plant Visits",
    kicker: "Transfers of technical data under Part 125.",
    items: [
      {
        code: "§ 125.4(b)",
        slug: "125-4-b",
        name: "Technical Data Export Exemptions",
        cfr: "22 CFR § 125.4(b)",
        description: "Twelve numbered pathways for exporting technical data without a specific license — ranging from DOD-directed requests to routine copies for existing recipients.",
        subs: [
          {
            code: "§ 125.4(b)(1)",
            slug: "125-4-b-1",
            name: "Written Request From DOD",
            cfr: "22 CFR § 125.4(b)(1)",
            description: "Technical data exported pursuant to a written request from the Department of Defense.",
          },
          {
            code: "§ 125.4(b)(2)",
            slug: "125-4-b-2",
            name: "In Furtherance of a TAA or MLA",
            cfr: "22 CFR § 125.4(b)(2)",
            description: "Technical data in furtherance of a TAA or MLA of items previously authorized.",
          },
          {
            code: "§ 125.4(b)(3)",
            slug: "125-4-b-3",
            name: "Contract Between Exporter and a U.S. Agency",
            cfr: "22 CFR § 125.4(b)(3)",
            description: "Technical data exported pursuant to a contract between the exporter and a U.S. agency.",
          },
          {
            code: "§ 125.4(b)(4)",
            slug: "125-4-b-4",
            name: "Copies to Same Recipient of Previously-Authorized Items",
            cfr: "22 CFR § 125.4(b)(4)",
            description: "Copies of technical data to the same recipient of items previously authorized.",
          },
          {
            code: "§ 125.4(b)(5)",
            slug: "125-4-b-5",
            name: "Basic Operations, Maintenance & Training Information",
            cfr: "22 CFR § 125.4(b)(5)",
            description: "Basic operations, maintenance, and training information of items previously authorized.",
          },
          {
            code: "§ 125.4(b)(6)",
            slug: "125-4-b-6",
            name: "Firearms .50 Caliber and Below",
            cfr: "22 CFR § 125.4(b)(6)",
            description: "Technical data for firearms not in excess of caliber .50 and their ammunition — excluding detailed design, development, production, or manufacturing information.",
          },
          {
            code: "§ 125.4(b)(7)",
            slug: "125-4-b-7",
            name: "Return to Original Source of Import",
            cfr: "22 CFR § 125.4(b)(7)",
            description: "Technical data returned to the original source of the import.",
          },
          {
            code: "§ 125.4(b)(8)",
            slug: "125-4-b-8",
            name: "Related to Classified Information of Previously-Authorized Items",
            cfr: "22 CFR § 125.4(b)(8)",
            description: "Technical data related to classified information of items previously authorized.",
          },
          {
            code: "§ 125.4(b)(9)",
            slug: "125-4-b-9",
            name: "U.S. Person (or Foreign Employee) Traveling Abroad",
            cfr: "22 CFR § 125.4(b)(9)",
            description: "Technical data taken abroad by a U.S. person, or a foreign employee of a U.S. person, traveling abroad.",
          },
          {
            code: "§ 125.4(b)(10)",
            slug: "125-4-b-10",
            name: "U.S. University to a Foreign Employee",
            cfr: "22 CFR § 125.4(b)(10)",
            description: "Technical data provided by a U.S. university to a foreign employee.",
          },
          {
            code: "§ 125.4(b)(11)",
            slug: "125-4-b-11",
            name: "Under Arrangement With DOD, NASA, or DOE",
            cfr: "22 CFR § 125.4(b)(11)",
            description: "Technical data under an arrangement with DOD, DOE, or NASA that requires such exports — requires a written exemption from DDTC.",
          },
          {
            code: "§ 125.4(b)(13)",
            slug: "125-4-b-13",
            name: "Approved for Unlimited Distribution",
            cfr: "22 CFR § 125.4(b)(13)",
            description: "Technical data approved by the U.S. Government for unlimited distribution of items previously authorized.",
          },
        ],
      },
      {
        code: "§ 125.4(c)",
        slug: "125-4-c",
        name: "DOD Bid Proposal to NATO / AJS",
        cfr: "22 CFR § 125.4(c)",
        description: "Defense services and related unclassified technical data provided to NATO, Australian, Japanese, or Swedish nationals in response to a written DOD quote or bid proposal request. Limited to build-to-print, build/design-to-specification, and basic research.",
      },
      {
        code: "§ 125.5",
        slug: "125-5",
        name: "Plant Visits — Oral, Visual, and Documentary Disclosures",
        cfr: "22 CFR § 125.5",
        description: "Three disclosure pathways tied to previously-approved visits to classified or unclassified facilities.",
        subs: [
          {
            code: "§ 125.5(a)",
            slug: "125-5-a",
            name: "Unclassified Disclosure at a Classified Plant",
            cfr: "22 CFR § 125.5(a)",
            description: "Oral or visual disclosure of unclassified technical data during a previously-approved visit to a classified plant.",
          },
          {
            code: "§ 125.5(b)",
            slug: "125-5-b",
            name: "Classified Disclosure at a Classified Plant",
            cfr: "22 CFR § 125.5(b)",
            description: "Disclosure of classified technical data during a previously-approved visit to a classified plant.",
          },
          {
            code: "§ 125.5(c)",
            slug: "125-5-c",
            name: "Unclassified Disclosure at Any Plant",
            cfr: "22 CFR § 125.5(c)",
            description: "Disclosure of unclassified technical data during a previously-approved visit to a classified or unclassified plant.",
          },
        ],
      },
    ],
  },
  {
    id: "country-specific",
    label: "Country-Specific Exemptions",
    kicker: "Canada, the United Kingdom, and Australia — including the AUKUS trilateral exemption.",
    items: [
      {
        code: "§ 126.5",
        slug: "126-5",
        name: "Canadian Exemption",
        cfr: "22 CFR § 126.5",
        description: "Temporary imports from Canada, exports to Canada, and reexports or retransfers within Canada.",
        subs: [
          {
            code: "§ 126.5(a)",
            slug: "126-5-a",
            name: "Temporary Imports From Canada",
            cfr: "22 CFR § 126.5(a)",
            description: "The Canadian exemption for temporary imports from Canada.",
          },
          {
            code: "§ 126.5(b)",
            slug: "126-5-b",
            name: "Exports to Canada",
            cfr: "22 CFR § 126.5(b)",
            description: "The Canadian exemption for exports to Canada.",
          },
          {
            code: "§ 126.5(d)",
            slug: "126-5-d",
            name: "Reexports or Retransfers in Canada",
            cfr: "22 CFR § 126.5(d)",
            description: "The Canadian exemption for reexports or retransfers within Canada.",
          },
        ],
      },
      {
        code: "§ 126.7",
        slug: "126-7",
        name: "AUKUS Exemption — U.S., Australia, and the UK",
        cfr: "22 CFR § 126.7",
        description: "AUKUS trilateral exemption for defense articles, defense services, and brokering activities among the United States, Australia, and the United Kingdom — subject to authorized-user, physical-territory, and value limits.",
      },
      {
        code: "§ 126.16",
        slug: "126-16",
        name: "Australia Exemption",
        cfr: "22 CFR § 126.16",
        description: "The bilateral Australia exemption for defense trade with authorized Australian community members.",
      },
      {
        code: "§ 126.17",
        slug: "126-17",
        name: "United Kingdom Exemption",
        cfr: "22 CFR § 126.17",
        description: "The bilateral UK exemption for defense trade with authorized UK community members.",
      },
    ],
  },
  {
    id: "deemed-reexports",
    label: "Deemed Reexports to Individuals Abroad",
    kicker: "Release of technical data or defense services to foreign-national employees of an authorized end user.",
    items: [
      {
        code: "§ 126.18(a)–(c)",
        slug: "126-18-a-c",
        name: "Deemed Reexport — Security Protocols",
        cfr: "22 CFR § 126.18(a)–(c)",
        description: "Deemed reexport to an end user's employees, subject to the security protocol and screening requirements of paragraphs (a) through (c).",
      },
      {
        code: "§ 126.18(d)",
        slug: "126-18-d",
        name: "Deemed Reexport — NATO and Other Allies",
        cfr: "22 CFR § 126.18(d)",
        description: "Deemed reexport to an end user's employees who are nationals of NATO countries and other close allies.",
      },
      {
        code: "§ 126.18(e)",
        slug: "126-18-e",
        name: "Deemed Reexport — Classified Items to Australian / UK Dual Nationals",
        cfr: "22 CFR § 126.18(e)",
        description: "Deemed reexport or retransfer of classified items to a dual citizen of Australia or the UK who holds a security clearance.",
      },
    ],
  },
  {
    id: "usg-fms",
    label: "U.S. Government & Foreign Military Sales",
    kicker: "Activities performed by — or at the direction of — the U.S. Government, plus Foreign Military Sales.",
    items: [
      {
        code: "§ 126.4(a)(1)",
        slug: "126-4-a-1",
        name: "USG Activities — Official Use",
        cfr: "22 CFR § 126.4(a)(1)",
        description: "Activities performed by the U.S. Government for official use by the U.S. Government.",
      },
      {
        code: "§ 126.4(a)(2)",
        slug: "126-4-a-2",
        name: "USG Activities — International Agreement",
        cfr: "22 CFR § 126.4(a)(2)",
        description: "Activities performed by the U.S. Government for carrying out an international agreement.",
      },
      {
        code: "§ 126.4(a)(3)",
        slug: "126-4-a-3",
        name: "USG Activities — Foreign Assistance or Sales Program",
        cfr: "22 CFR § 126.4(a)(3)",
        description: "Activities performed by the U.S. Government for carrying out any foreign assistance or sales program authorized by law.",
      },
      {
        code: "§ 126.4(a)(4)",
        slug: "126-4-a-4",
        name: "USG Activities — DOD Security Cooperation",
        cfr: "22 CFR § 126.4(a)(4)",
        description: "Activities performed by the U.S. Government for any other DOD security cooperation programs and activities authorized by law.",
      },
      {
        code: "§ 126.4(b)",
        slug: "126-4-b",
        name: "Activities at the Direction of the U.S. Government",
        cfr: "22 CFR § 126.4(b)",
        description: "Activities performed by another person for the U.S. Government or another entity at the direction of the U.S. Government.",
      },
      {
        code: "§ 126.4(c)",
        slug: "126-4-c",
        name: "Return of Previously Exported Defense Article",
        cfr: "22 CFR § 126.4(c)",
        description: "Return to the United States of a defense article previously exported pursuant to § 126.4.",
      },
      {
        code: "§ 126.6",
        slug: "126-6",
        name: "Foreign Military Sales, Delivery to Foreign Reps, and Foreign Military Aircraft/Vessels",
        cfr: "22 CFR § 126.6",
        description: "Foreign Military Sales program transfers under an executed Letter of Offer and Acceptance; delivery in the U.S. of DOD-sold or DOD-leased defense articles to foreign country representatives (exported via that country's military aircraft or vessel); and entry of foreign military aircraft and naval vessels into the U.S.",
      },
    ],
  },
];

export function buildItarExemptionIndex() {
  const idx = {};
  for (const group of ITAR_EXEMPTION_GROUPS) {
    for (const item of group.items) {
      idx[item.slug] = { ...item, group };
      if (item.subs) {
        for (const sub of item.subs) {
          idx[sub.slug] = { ...sub, group, parent: item };
        }
      }
    }
  }
  return idx;
}
